// Game logic for JumpBean - Challenge validation and scoring algorithms
// Re-export content gating functions
export * from "./content-gating.js";
// Re-export adaptive difficulty system
export * from "./adaptive/index.js";
/**
 * Feedback messages for correct answers based on stars earned
 */
export const CORRECT_FEEDBACK_MESSAGES = {
    3: { title: "Amazing!", subtitle: "Perfect on your first try!" },
    2: { title: "Great job!", subtitle: "You're doing wonderfully!" },
    1: { title: "You did it!", subtitle: "Keep up the great work!" },
};
/**
 * Encouraging feedback messages for incorrect answers
 * Never punishing - always supportive
 */
export const ENCOURAGE_FEEDBACK_MESSAGES = [
    { title: "Try again!", subtitle: "You've got this!" },
    { title: "Almost there!", subtitle: "Give it another go!" },
    { title: "Keep trying!", subtitle: "You can do it!" },
    { title: "So close!", subtitle: "Try one more time!" },
];
/**
 * Validate a counting challenge answer
 */
export function validateCountingAnswer(content, userAnswer) {
    return {
        isCorrect: userAnswer === content.correctAnswer,
    };
}
/**
 * Validate a number recognition challenge answer
 */
export function validateNumberRecognitionAnswer(content, userAnswer) {
    return {
        isCorrect: userAnswer === content.correctAnswer,
    };
}
/**
 * Validate an addition challenge answer
 */
export function validateAdditionAnswer(content, userAnswer) {
    const correctAnswer = content.operand1 + content.operand2;
    return {
        isCorrect: userAnswer === correctAnswer,
    };
}
/**
 * Validate a subtraction challenge answer
 */
export function validateSubtractionAnswer(content, userAnswer) {
    const correctAnswer = content.operand1 - content.operand2;
    return {
        isCorrect: userAnswer === correctAnswer,
    };
}
/**
 * Validate a beginning sound (phonics) challenge answer
 */
export function validateBeginningSoundAnswer(content, userAnswer) {
    return {
        isCorrect: userAnswer.toLowerCase() === content.correctAnswer.toLowerCase(),
    };
}
/**
 * Validate a letter recognition challenge answer
 */
export function validateLetterRecognitionAnswer(content, userAnswer) {
    // Use correctAnswer if present, otherwise fall back to targetLetter
    const correctAnswer = content.correctAnswer ?? content.targetLetter;
    if (!correctAnswer || !userAnswer) {
        return { isCorrect: false };
    }
    return {
        isCorrect: userAnswer.toLowerCase() === correctAnswer.toLowerCase(),
    };
}
/**
 * Validate a word building challenge answer
 */
export function validateWordBuildingAnswer(content, userAnswer) {
    return {
        isCorrect: userAnswer.toLowerCase() === content.correctAnswer.toLowerCase(),
    };
}
/**
 * Validate a pattern sequence challenge answer
 */
export function validatePatternSequenceAnswer(content, userAnswer) {
    const normalizedUser = typeof userAnswer === "string" ? userAnswer.toLowerCase() : userAnswer;
    const normalizedCorrect = typeof content.correctAnswer === "string"
        ? content.correctAnswer.toLowerCase()
        : content.correctAnswer;
    return {
        isCorrect: normalizedUser === normalizedCorrect,
    };
}
/**
 * Validate a color pattern challenge answer
 */
export function validateColorPatternAnswer(content, userAnswer) {
    // Handle both camelCase and snake_case field names
    const correctAnswer = content.correctAnswer ?? content.correct_answer;
    if (!correctAnswer || !userAnswer) {
        return { isCorrect: false };
    }
    return {
        isCorrect: userAnswer.toLowerCase() === correctAnswer.toLowerCase(),
    };
}
/**
 * Validate a key finding challenge answer
 */
export function validateKeyFindingAnswer(content, pressedKey) {
    // Handle both camelCase and snake_case field names
    const targetKey = content.targetKey ?? content.target_key;
    if (!targetKey || !pressedKey) {
        return { isCorrect: false };
    }
    return {
        isCorrect: pressedKey.toLowerCase() === targetKey.toLowerCase(),
    };
}
/**
 * Validate a word typing challenge answer
 */
export function validateWordTypingAnswer(content, typedWord) {
    // Handle both camelCase and snake_case field names
    const targetWord = content.targetWord ?? content.target_word;
    if (!targetWord || !typedWord) {
        return { isCorrect: false };
    }
    return {
        isCorrect: typedWord.toLowerCase() === targetWord.toLowerCase(),
    };
}
/**
 * Validate a sequence typing challenge answer
 * The user types a sequence of letters (e.g., "A", "B" -> "AB")
 */
export function validateSequenceTypingAnswer(content, typedSequence) {
    // Handle both camelCase and snake_case field names
    const targetSequence = content.targetSequence ?? content.target_sequence;
    if (!targetSequence || !typedSequence) {
        return { isCorrect: false };
    }
    // Join the target sequence array to compare with the typed string
    const expectedSequence = targetSequence.join("");
    return {
        isCorrect: typedSequence.toLowerCase() === expectedSequence.toLowerCase(),
    };
}
/**
 * Execute maze commands and check if the goal is reached
 */
export function executeMazeCommands(gridSize, start, end, obstacles, commands) {
    let current = { ...start };
    const [width, height] = gridSize;
    const obstacleSet = new Set(obstacles.map((o) => `${o.x},${o.y}`));
    for (const command of commands) {
        let next = { ...current };
        switch (command) {
            case "up":
                next.y = Math.max(0, current.y - 1);
                break;
            case "down":
                next.y = Math.min(height - 1, current.y + 1);
                break;
            case "left":
                next.x = Math.max(0, current.x - 1);
                break;
            case "right":
                next.x = Math.min(width - 1, current.x + 1);
                break;
        }
        // Check for obstacle collision
        if (obstacleSet.has(`${next.x},${next.y}`)) {
            return {
                finalPosition: current,
                reachedGoal: false,
                hitObstacle: true,
            };
        }
        current = next;
    }
    return {
        finalPosition: current,
        reachedGoal: current.x === end.x && current.y === end.y,
        hitObstacle: false,
    };
}
/**
 * Validate a maze challenge answer
 */
export function validateMazeAnswer(content, commands) {
    const result = executeMazeCommands(content.gridSize, { x: content.start[0], y: content.start[1] }, { x: content.end[0], y: content.end[1] }, content.obstacles.map(([x, y]) => ({ x, y })), commands);
    return {
        isCorrect: result.reachedGoal,
        feedback: result.hitObstacle
            ? "Hit an obstacle!"
            : result.reachedGoal
                ? undefined
                : "Didn't reach the goal",
    };
}
/**
 * Validate a single command challenge answer
 */
export function validateSingleCommandAnswer(content, commands) {
    // Handle both camelCase and snake_case field names
    const gridSize = content.gridSize ?? content.grid_size;
    if (!gridSize) {
        return { isCorrect: false, feedback: "Invalid challenge configuration" };
    }
    const result = executeMazeCommands(gridSize, { x: content.start[0], y: content.start[1] }, { x: content.end[0], y: content.end[1] }, content.obstacles.map(([x, y]) => ({ x, y })), commands);
    return {
        isCorrect: result.reachedGoal,
        feedback: result.hitObstacle
            ? "Hit an obstacle!"
            : result.reachedGoal
                ? undefined
                : "Keep trying! Help Pixel reach the star!",
    };
}
/**
 * Generic challenge validator that routes to the appropriate validator
 */
export function validateChallengeAnswer(challenge, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
userAnswer) {
    const content = challenge.content;
    switch (challenge.type) {
        case "counting":
            return validateCountingAnswer(content, userAnswer);
        case "number_recognition":
            return validateNumberRecognitionAnswer(content, userAnswer);
        case "addition":
            return validateAdditionAnswer(content, userAnswer);
        case "subtraction":
            return validateSubtractionAnswer(content, userAnswer);
        case "beginning_sound":
            return validateBeginningSoundAnswer(content, userAnswer);
        case "letter_recognition":
            return validateLetterRecognitionAnswer(content, userAnswer);
        case "word_building":
            return validateWordBuildingAnswer(content, userAnswer);
        case "pattern_sequence":
            return validatePatternSequenceAnswer(content, userAnswer);
        case "color_pattern":
            return validateColorPatternAnswer(content, userAnswer);
        case "key_finding":
            return validateKeyFindingAnswer(content, userAnswer);
        case "word_typing":
            return validateWordTypingAnswer(content, userAnswer);
        case "sequence_typing":
            return validateSequenceTypingAnswer(content, userAnswer);
        case "maze":
            return validateMazeAnswer(content, userAnswer);
        case "single_command":
            return validateSingleCommandAnswer(content, userAnswer);
        default:
            return { isCorrect: false, feedback: "Unknown challenge type" };
    }
}
/**
 * Calculate optimal time for a challenge based on type and difficulty
 * Returns time in seconds
 */
export function getOptimalTime(challengeType, difficulty) {
    // Base times for each challenge type (in seconds)
    const baseTimes = {
        counting: 15,
        number_recognition: 10,
        addition: 20,
        subtraction: 20,
        comparison: 15,
        beginning_sound: 15,
        letter_recognition: 10,
        word_building: 30,
        pattern_sequence: 20,
        color_pattern: 15,
        key_finding: 10,
        sequence_typing: 20,
        word_typing: 30,
        maze: 60,
        single_command: 30,
    };
    const baseTime = baseTimes[challengeType] ?? 20;
    // Adjust based on difficulty (1-5 scale)
    return baseTime * (1 + (difficulty - 1) * 0.2);
}
/**
 * Determine if a badge should be awarded after completing a challenge
 */
export function shouldAwardBadge(levelId, completedChallengeIds, totalChallengesInLevel) {
    // Badge is awarded when all challenges in a level are completed
    return completedChallengeIds.length >= totalChallengesInLevel;
}
/**
 * Calculate streak days based on play history
 */
export function calculateStreak(lastPlayedAt, currentDate) {
    if (!lastPlayedAt) {
        return { currentStreak: 1, isNewDay: true };
    }
    const lastPlayed = new Date(lastPlayedAt);
    const today = new Date(currentDate);
    // Reset time to midnight for comparison
    lastPlayed.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - lastPlayed.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
        // Same day, streak continues but not a new day
        return { currentStreak: 0, isNewDay: false };
    }
    else if (diffDays === 1) {
        // Consecutive day, increment streak
        return { currentStreak: 1, isNewDay: true };
    }
    else {
        // Streak broken, reset to 1
        return { currentStreak: 1, isNewDay: true };
    }
}
/**
 * Validate an answer and get feedback
 *
 * This is the main entry point for the answer submission system.
 * It validates the answer and returns structured feedback.
 *
 * @param challenge - The challenge being answered
 * @param userAnswer - The user's submitted answer
 * @param attempts - Current attempt count (1-based)
 * @returns Validation result with feedback information
 */
export function validateAnswer(challenge, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
userAnswer, attempts = 1) {
    // Validate the answer
    const validation = validateChallengeAnswer(challenge, userAnswer);
    // Calculate stars if correct (based on attempt count)
    // Uses simple rule: 1st try = 3 stars, 2nd try = 2 stars, 3+ tries = 1 star
    let starsEarned;
    if (validation.isCorrect) {
        if (attempts === 1)
            starsEarned = 3;
        else if (attempts === 2)
            starsEarned = 2;
        else
            starsEarned = 1;
    }
    // Get appropriate feedback message
    let message;
    if (validation.isCorrect && starsEarned !== undefined) {
        message = CORRECT_FEEDBACK_MESSAGES[starsEarned];
    }
    else {
        // Rotate through encourage messages based on attempts
        // Always use a valid index (0-3) from the ENCOURAGE_FEEDBACK_MESSAGES array
        const messageIndex = Math.abs((attempts - 1) % ENCOURAGE_FEEDBACK_MESSAGES.length);
        const encourageMessage = ENCOURAGE_FEEDBACK_MESSAGES[messageIndex];
        message = encourageMessage !== undefined
            ? encourageMessage
            : { title: "Try again!", subtitle: "You've got this!" };
    }
    const feedback = {
        type: validation.isCorrect ? "correct" : "incorrect",
        starsEarned,
        message,
        attempts,
    };
    return { validation, feedback };
}
//# sourceMappingURL=index.js.map